import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode, type SelectHTMLAttributes } from 'react';

/** Acima disto a lista vira campo com busca enquanto digita (autocomplete); abaixo fica o seletor nativo. */
export const SEARCHABLE_FROM = 12;
const MAX_SHOWN = 200;

type Opt = { value: string; label: string; disabled?: boolean; group?: string };

const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/**
 * Seletor da plataforma: o mesmo uso do <select> (value, onChange com e.target.value, <option>, <optgroup>), mas listas
 * grandes viram um campo com busca enquanto digita, navegável por teclado (setas, Enter, Esc) e lido por leitores de tela
 * como combobox. Listas pequenas e de seleção múltipla continuam no seletor nativo.
 */
export function Select({ children, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { children?: ReactNode }) {
  // as opções são lidas do próprio <select> montado (vale para <option> vindas de subcomponentes); ele continua na página,
  // escondido, como fonte das opções quando a lista vira campo com busca
  const ref = useRef<HTMLSelectElement>(null);
  const [opts, setOpts] = useState<Opt[] | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let last = '';
    const read = () => {
      const list: Opt[] = [...el.options].map((o) => ({
        value: o.value,
        label: o.text,
        disabled: o.disabled,
        group: o.parentElement instanceof HTMLOptGroupElement ? o.parentElement.label : undefined,
      }));
      const key = JSON.stringify(list);
      if (key !== last) {
        last = key;
        setOpts(list);
      }
    };
    read();
    const mo = new MutationObserver(read);
    mo.observe(el, { childList: true, subtree: true, characterData: true, attributes: true });
    return () => mo.disconnect();
  }, []);
  const combo = !props.multiple && !!opts && opts.length > SEARCHABLE_FROM;
  return (
    <>
      {combo ? (
        <select ref={ref} hidden aria-hidden="true" tabIndex={-1} value={props.value} onChange={() => undefined}>
          {children}
        </select>
      ) : (
        <select ref={ref} {...props}>
          {children}
        </select>
      )}
      {combo && <Combobox options={opts} {...props} />}
    </>
  );
}

function Combobox({ options, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { options: Opt[] }) {
  const listId = useId();
  const box = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const value = props.value == null ? '' : String(props.value);
  const selected = options.find((o) => o.value === value);
  const shown = useMemo(() => {
    const f = fold(q.trim());
    return (f ? options.filter((o) => fold(`${o.label} ${o.group ?? ''}`).includes(f)) : options).slice(0, MAX_SHOWN);
  }, [options, q]);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);
  useEffect(() => {
    list.current?.querySelector<HTMLElement>(`[data-i="${active}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  const pick = (o: Opt | undefined) => {
    if (!o || o.disabled) return;
    setOpen(false);
    setQ('');
    if (o.value !== value) {
      props.onChange?.({ target: { value: o.value }, currentTarget: { value: o.value } } as unknown as ChangeEvent<HTMLSelectElement>);
    }
  };
  const start = () => {
    if (props.disabled) return;
    setQ('');
    setActive(Math.max(0, options.findIndex((o) => o.value === value)));
    setOpen(true);
  };

  let lastGroup: string | undefined;
  return (
    <div ref={box} className={`cbx${props.className ? ` ${props.className}` : ''}`} style={props.style}>
      <input
        id={props.id}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && shown[active] ? `${listId}-${active}` : undefined}
        aria-label={props['aria-label']}
        aria-labelledby={props['aria-labelledby']}
        aria-describedby={props['aria-describedby']}
        aria-invalid={props['aria-invalid']}
        aria-required={props['aria-required'] ?? props.required}
        name={props.name}
        disabled={props.disabled}
        title={props.title}
        value={open ? q : (selected?.label ?? '')}
        placeholder={open ? (selected?.label ?? '') : undefined}
        onFocus={start}
        onClick={() => !open && start()}
        onChange={(e) => {
          setQ(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!open) start();
            else setActive((i) => Math.min(shown.length - 1, i + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(0, i - 1));
          } else if (e.key === 'Enter') {
            if (open) {
              e.preventDefault();
              pick(shown[active]);
            }
          } else if (e.key === 'Escape') {
            if (open) {
              e.preventDefault();
              e.stopPropagation();
              setOpen(false);
              setQ('');
            }
          } else if (e.key === 'Tab') {
            setOpen(false);
            setQ('');
          }
        }}
      />
      <span className="cbx-caret" aria-hidden="true" />
      {open && (
        <ul ref={list} id={listId} role="listbox" className="cbx-list">
          {shown.length === 0 && (
            <li className="cbx-empty" role="presentation">
              —
            </li>
          )}
          {shown.map((o, i) => {
            const head = o.group && o.group !== lastGroup ? o.group : null;
            lastGroup = o.group;
            return (
              <li key={`${o.group ?? ''}|${o.value}|${i}`} role="presentation">
                {head && <div className="cbx-group">{head}</div>}
                <div
                  id={`${listId}-${i}`}
                  data-i={i}
                  role="option"
                  aria-selected={o.value === value}
                  aria-disabled={o.disabled || undefined}
                  className={`cbx-opt${i === active ? ' on' : ''}${o.value === value ? ' sel' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(o)}
                  // o foco fica no campo (aria-activedescendant); a opção só responde se receber foco por outro meio
                  tabIndex={-1}
                  onKeyDown={(e) => e.key === 'Enter' && pick(o)}
                >
                  {o.label || '—'}
                </div>
              </li>
            );
          })}
          {shown.length === MAX_SHOWN && (
            <li className="cbx-empty" role="presentation">
              …
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
